/**
 * CalendarPlannerTab — Post-finalization meal scheduling.
 *
 * Displays finalized meals as draggable tiles. Primary users drag them
 * onto a Sunday–Saturday calendar grid with Breakfast/Lunch/Dinner rows.
 * All tribe members can view the schedule; only primary users can edit.
 *
 * Supports 1–4 week planning windows (household setting).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useHousehold } from '../../hooks/useHousehold';
import { useApi } from '../../hooks/useApi';
import {
  getWeekId, getNextWeekId, getWeekDateRange, formatWeekRange,
} from '@tabletryb/shared';
import type { CalendarAssignments, MealSlotType } from '@tabletryb/shared';
import { Loader, Check, CalendarDays } from 'lucide-react';
import '../../styles/calendar-planner.css';

// ── Types ──────────────────────────────────────────────────────────────────
interface RecipeRef {
  recipeId: string;
  title: string;
  image: string;
  readyInMinutes: number;
  servings: number;
}

interface Meal {
  mealId: string;
  recipe: RecipeRef;
}

interface WeekData {
  weekId: string;
  meals: Meal[];            // finalized meals
  assignments: CalendarAssignments;
  status: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MEAL_SLOTS: MealSlotType[] = ['breakfast', 'lunch', 'dinner'];
const SLOT_LABELS: Record<MealSlotType, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

/** Get Sunday–Saturday dates for a given ISO weekId */
function getWeekDaysSunToSat(weekId: string): Date[] {
  const { start: monday } = getWeekDateRange(weekId);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() - 1);

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(sunday);
    day.setUTCDate(sunday.getUTCDate() + i);
    days.push(day);
  }
  return days;
}

/** Format a date as YYYY-MM-DD for slot keys */
function formatDateKey(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Format a date as "Apr 6" for display */
function formatDayDate(date: Date): string {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

// ── Component ──────────────────────────────────────────────────────────────
const CalendarPlannerTab: React.FC = () => {
  const { user } = useAuth();
  const { household } = useHousehold();
  const api = useApi();
  const isPrimary = user?.role === 'primary';
  const householdId = user?.householdId;

  const [weekDataMap, setWeekDataMap] = useState<Record<string, WeekData>>({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [draggedMealId, setDraggedMealId] = useState<string | null>(null);
  const [dragSourceSlot, setDragSourceSlot] = useState<{ weekId: string; slotKey: string } | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // planningWindowWeeks lives in the SETTINGS record, returned as household.settings
  const settings = (household as any)?.settings;
  const planningWindowWeeks: number = settings?.planningWindowWeeks || 1;

  // Compute weekIds for the planning window
  const weekIds = useMemo(() => {
    const ids: string[] = [];
    let currentId = getWeekId();
    for (let i = 0; i < planningWindowWeeks; i++) {
      ids.push(currentId);
      currentId = getNextWeekId(currentId);
    }
    return ids;
  }, [planningWindowWeeks]);

  // Load all data for all weeks
  const loadData = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        weekIds.map(async (weekId) => {
          const [mealData, calData] = await Promise.all([
            api.get<{
              weekId: string;
              status: string;
              meals: Meal[];
              finalizedMealIds: string[];
            }>(`/v1/households/${householdId}/meal-plans/${weekId}`),
            api.get<{ assignments: CalendarAssignments }>(
              `/v1/households/${householdId}/calendar/${weekId}`
            ),
          ]);

          // Filter to only finalized meals
          const finalizedIds = new Set(mealData.finalizedMealIds || []);
          const finalizedMeals = (mealData.meals || []).filter((m) =>
            finalizedIds.has(m.mealId)
          );

          return {
            weekId,
            meals: finalizedMeals,
            assignments: calData.assignments || {},
            status: mealData.status,
          } as WeekData;
        })
      );

      const map: Record<string, WeekData> = {};
      for (const wd of results) {
        map[wd.weekId] = wd;
      }
      setWeekDataMap(map);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [householdId, weekIds]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Save calendar assignments for a specific week
  const saveAssignments = useCallback(
    async (weekId: string, assignments: CalendarAssignments) => {
      if (!householdId) return;
      setSaveStatus('saving');
      try {
        await api.put(`/v1/households/${householdId}/calendar/${weekId}`, {
          assignments,
        });
        setSaveStatus('saved');
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Failed to save calendar:', err);
        setSaveStatus('idle');
      }
    },
    // eslint-disable-next-line
    [householdId]
  );

  // ── Drag & Drop handlers ───────────────────────────────────────────────
  const handleDragStart = (mealId: string, sourceSlot?: { weekId: string; slotKey: string }) => {
    setDraggedMealId(mealId);
    setDragSourceSlot(sourceSlot || null);
  };

  const handleDragEnd = () => {
    setDraggedMealId(null);
    setDragSourceSlot(null);
    setDragOverSlot(null);
  };

  const handleDragOver = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    setDragOverSlot(slotKey);
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = (weekId: string, slotKey: string) => {
    if (!draggedMealId || !isPrimary) return;
    setDragOverSlot(null);

    setWeekDataMap((prev) => {
      const weekData = prev[weekId];
      if (!weekData) return prev;

      const newAssignments = { ...weekData.assignments, [slotKey]: draggedMealId };

      // If dragged from another slot in the same week, clear the source
      if (dragSourceSlot && dragSourceSlot.weekId === weekId && dragSourceSlot.slotKey !== slotKey) {
        delete newAssignments[dragSourceSlot.slotKey];
      }

      saveAssignments(weekId, newAssignments);

      let updatedMap = {
        ...prev,
        [weekId]: { ...weekData, assignments: newAssignments },
      };

      // If dragged from a different week, clear the source in that week
      if (dragSourceSlot && dragSourceSlot.weekId !== weekId) {
        const srcWeek = prev[dragSourceSlot.weekId];
        if (srcWeek) {
          const srcAssignments = { ...srcWeek.assignments };
          delete srcAssignments[dragSourceSlot.slotKey];
          saveAssignments(dragSourceSlot.weekId, srcAssignments);
          updatedMap = {
            ...updatedMap,
            [dragSourceSlot.weekId]: { ...srcWeek, assignments: srcAssignments },
          };
        }
      }

      return updatedMap;
    });

    setDraggedMealId(null);
    setDragSourceSlot(null);
  };

  const handleRemove = (weekId: string, slotKey: string) => {
    if (!isPrimary) return;

    setWeekDataMap((prev) => {
      const weekData = prev[weekId];
      if (!weekData) return prev;

      const newAssignments = { ...weekData.assignments };
      delete newAssignments[slotKey];
      saveAssignments(weekId, newAssignments);
      return {
        ...prev,
        [weekId]: { ...weekData, assignments: newAssignments },
      };
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="tab-panel" style={{ textAlign: 'center', padding: '3rem' }}>
        <Loader size={24} className="spin" />
        <p>Loading calendar...</p>
      </div>
    );
  }

  // Check if any week has finalized meals
  const hasAnyFinalized = weekIds.some(
    (wid) => weekDataMap[wid]?.status === 'finalized'
  );

  if (!hasAnyFinalized) {
    return (
      <div className="tab-panel">
        <div className="empty-state">
          <CalendarDays size={48} />
          <h3>No finalized meals yet</h3>
          <p>
            Finalize your meal selections in the Results tab first, then come
            back here to schedule them on the calendar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-panel calendar-planner">
      <div className="tab-header">
        <div>
          <h2>Calendar Planner</h2>
          <p className="tab-subtitle">
            {isPrimary
              ? 'Drag finalized meals onto the calendar to plan your week.'
              : "Your tribe's meal schedule for the week."}
          </p>
        </div>
        <div className="calendar-save-indicator">
          {saveStatus === 'saving' && (
            <>
              <Loader size={14} className="spin" /> Saving...
            </>
          )}
          {saveStatus === 'saved' && (
            <span className="calendar-save-indicator saved">
              <Check size={14} /> Saved
            </span>
          )}
        </div>
      </div>

      {weekIds.map((weekId) => {
        const weekData = weekDataMap[weekId];
        if (!weekData || weekData.status !== 'finalized') {
          return (
            <div key={weekId} className="calendar-week-section">
              <div className="calendar-week-header">
                {formatWeekRange(weekId)}
              </div>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Meals not yet finalized for this week.
              </p>
            </div>
          );
        }

        const days = getWeekDaysSunToSat(weekId);
        const { meals, assignments } = weekData;

        // Build a lookup from mealId → Meal
        const mealById: Record<string, Meal> = {};
        for (const m of meals) {
          mealById[m.mealId] = m;
        }

        // Track which meals are already placed on the calendar
        const placedMealIds = new Set(Object.values(assignments));

        return (
          <div key={weekId} className="calendar-week-section">
            <div className="calendar-week-header">
              {formatWeekRange(weekId)}
            </div>

            {/* Meal pool — draggable tiles */}
            {isPrimary && (
              <div className="calendar-meals-pool">
                {meals.map((meal) => (
                  <div
                    key={meal.mealId}
                    className={`calendar-meal-tile ${
                      draggedMealId === meal.mealId ? 'dragging' : ''
                    } ${placedMealIds.has(meal.mealId) ? 'placed' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(meal.mealId)}
                    onDragEnd={handleDragEnd}
                  >
                    {meal.recipe.image ? (
                      <img
                        src={meal.recipe.image}
                        alt=""
                        className="calendar-meal-tile-img"
                      />
                    ) : (
                      <span className="calendar-meal-tile-placeholder">🍽️</span>
                    )}
                    <span className="calendar-meal-tile-name">
                      {meal.recipe.title}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Calendar grid */}
            <div className="calendar-grid">
              {/* Header row: corner + 7 day headers */}
              <div className="calendar-grid-corner" />
              {days.map((date, i) => (
                <div key={i} className="calendar-grid-day-header">
                  {DAY_NAMES[i]}
                  <span className="calendar-grid-day-date">
                    {formatDayDate(date)}
                  </span>
                </div>
              ))}

              {/* One row per meal slot */}
              {MEAL_SLOTS.map((slot) => (
                <React.Fragment key={slot}>
                  <div className="calendar-grid-row-label">
                    {SLOT_LABELS[slot]}
                  </div>
                  {days.map((date) => {
                    const dateKey = formatDateKey(date);
                    const slotKey = `${dateKey}_${slot}`;
                    const assignedMealId = assignments[slotKey];
                    const assignedMeal = assignedMealId
                      ? mealById[assignedMealId]
                      : null;
                    const isOver = dragOverSlot === slotKey;

                    return (
                      <div
                        key={slotKey}
                        className={`calendar-slot ${
                          isPrimary ? 'droppable' : ''
                        } ${isOver ? 'drag-over' : ''}`}
                        onDragOver={
                          isPrimary
                            ? (e) => handleDragOver(e, slotKey)
                            : undefined
                        }
                        onDragLeave={isPrimary ? handleDragLeave : undefined}
                        onDrop={
                          isPrimary
                            ? () => handleDrop(weekId, slotKey)
                            : undefined
                        }
                      >
                        {assignedMeal ? (
                          <div
                            className="calendar-slot-meal"
                            draggable={isPrimary}
                            onDragStart={isPrimary ? () => handleDragStart(assignedMealId!, { weekId, slotKey }) : undefined}
                            onDragEnd={isPrimary ? handleDragEnd : undefined}
                          >
                            {assignedMeal.recipe.image ? (
                              <img
                                src={assignedMeal.recipe.image}
                                alt=""
                                className="calendar-slot-meal-img"
                              />
                            ) : null}
                            <span className="calendar-slot-meal-name">
                              {assignedMeal.recipe.title}
                            </span>
                            {isPrimary && (
                              <button
                                className="calendar-slot-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove(weekId, slotKey);
                                }}
                                title="Remove"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="calendar-slot-empty">
                            {isPrimary ? '—' : ''}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarPlannerTab;